import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { VisibilityPredicateType } from "../../../registry/visibility";
import { IntegrationKey } from "@erp/shared-types";

/**
 * Write File to Famous Step
 *
 * Drops a text file onto the filesystem Famous is hosted on, using the same
 * export-directory transfer the sales-order import uses: write a local file,
 * then upload it over SSH when a jump-box key is configured. When the Famous
 * share is mounted locally instead, the local write is the drop.
 *
 * The file body and name can be wired as inputs or typed in the step config.
 * A wired input wins over the config value.
 */
const fileContents = z
  .string()
  .max(1_000_000)
  .describe("Text written into the file");

const fileName = z
  .string()
  .max(255)
  .describe("File name to create. A single name, not a path.");

export const writeFileToFamousStep = defineStepConfig({
  type: "write_file_to_famous",
  name: "Write File to Famous",
  description:
    "Write a text string to a file on the Famous host, in the export directory used for sales order imports",
  category: "Integration",
  libraryModule: "Famous",
  visibility: [
    [
      {
        type: VisibilityPredicateType.Integration,
        integration: IntegrationKey.Famous,
      },
    ],
  ],

  inputSchema: z.object({
    contents: fileContents
      .optional()
      .describe(
        "Text to write. Overrides the contents set in the step configuration.",
      ),
    fileName: fileName
      .optional()
      .describe(
        "File name to create. Overrides the file name set in the step configuration.",
      ),
  }),

  outputSchema: z.object({
    fileName: z.string().describe("The file name that was written"),
    filePath: z
      .string()
      .describe(
        "Path of the file on the Famous host, or the local export path when that directory is mounted",
      ),
    bytesWritten: z.number().describe("Number of bytes written"),
  }),

  configSchema: z.object({
    contents: fileContents
      .optional()
      .describe("Text to write when the contents input is not wired"),
    fileName: fileName
      .optional()
      .describe("File name to create when the fileName input is not wired"),
    directory: z
      .string()
      .max(1024)
      .optional()
      .describe(
        "Absolute directory on the Famous host. Leave empty to use the Famous export directory.",
      ),
  }),

  emits: [] as const,

  execution: {
    retry: { maximumAttempts: 1 },
  },
});
